export const normalizeRecipeIngredientName = (value = '') =>
  String(value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const resolveName = (entry = {}) => {
  if (typeof entry === 'string') return entry;
  return (
    entry?.name ||
    entry?.ingredientName ||
    entry?.ingredient_name ||
    entry?.canonicalName ||
    entry?.malzeme ||
    entry?.urun_adi ||
    ''
  );
};

const resolveAmount = (entry = {}) => (
  entry?.needed?.amount ??
  entry?.canonicalNeed?.amount ??
  entry?.quantity ??
  entry?.amount ??
  entry?.miktar ??
  entry?.suggestedAmount ??
  1
);

const resolveUnit = (entry = {}) => (
  entry?.needed?.unit ||
  entry?.canonicalNeed?.unit ||
  entry?.unit ||
  entry?.birim ||
  'adet'
);

const SEASONING_HINTS = [
  'toz biber',
  'pul biber',
  'karabiber',
  'beyaz biber',
  'kimyon',
  'kekik',
  'nane',
  'sumak',
  'zerdeçal',
  'zerdecal',
  'muskat',
  'baharat',
  'kişniş',
  'kisnis',
  'defne',
  'tarçın',
  'tarcin',
  'fesleğen',
  'feslegen'
];

const OPTIONAL_ROLE_HINTS = ['optional', 'garnish', 'seasoning', 'spice', 'finish', 'fresh'];
const DOMINANT_ROLE_HINTS = ['dominant', 'primary', 'main', 'ana'];

export const getRecipeMissingIngredients = (recipe = {}) => {
  const candidates = [
    recipe?.match?.missingIngredients,
    recipe?.facts?.missingIngredients,
    recipe?.availability?.missingIngredients,
    recipe?.missingIngredients
  ];
  const seen = new Set();
  const result = [];

  candidates
    .filter(Array.isArray)
    .flat()
    .forEach((entry) => {
      const name = String(resolveName(entry) || '').trim();
      const normalizedName = normalizeRecipeIngredientName(name);
      const dictionaryId = typeof entry === 'object' ? String(entry?.dictionaryId || entry?.dictionary_id || '').trim() : '';
      const key = dictionaryId || normalizedName;
      if (!key || seen.has(key)) return;
      seen.add(key);
      result.push({
        ...(typeof entry === 'object' ? entry : {}),
        name,
        amount: resolveAmount(entry),
        unit: resolveUnit(entry),
        dictionaryId: dictionaryId || undefined,
        normalizedName
      });
    });

  return result;
};

export const getRecipeMissingNames = (recipe = {}) =>
  getRecipeMissingIngredients(recipe)
    .map((entry) => entry.name)
    .filter(Boolean);

export const isRecipeIngredientMissingByName = (name = '', missing = []) => {
  const normalized = normalizeRecipeIngredientName(name);
  if (!normalized) return false;
  return (Array.isArray(missing) ? missing : []).some((entry) => {
    const other = normalizeRecipeIngredientName(typeof entry === 'string' ? entry : resolveName(entry));
    return other && (other.includes(normalized) || normalized.includes(other));
  });
};

export const classifyMissingIngredient = (entry = {}) => {
  const nameKey = normalizeRecipeIngredientName(resolveName(entry));
  const roleText = [
    entry?.role,
    entry?.weightType,
    entry?.importance,
    entry?.priority,
    entry?.ingredientRole,
    entry?.componentRole
  ]
    .map((x) => String(x || '').toLocaleLowerCase('tr-TR'))
    .join(' ');
  const ingredientType = String(entry?.ingredientType || entry?.dictionary?.ingredientType || '').toLocaleLowerCase('tr-TR');
  const shoppingBehavior = String(entry?.shoppingBehavior || entry?.dictionary?.shoppingBehavior || '').toLocaleLowerCase('tr-TR');

  const isDominant =
    entry?.isDominant === true ||
    DOMINANT_ROLE_HINTS.some((token) => roleText.includes(token));

  const isSkippable =
    entry?.optional === true ||
    ingredientType === 'staple' ||
    ingredientType === 'spice' ||
    shoppingBehavior === 'optional' ||
    shoppingBehavior === 'never' ||
    OPTIONAL_ROLE_HINTS.some((token) => roleText.includes(token)) ||
    SEASONING_HINTS.some((token) => nameKey.includes(token));

  if (isDominant) {
    return {
      severity: 'critical',
      label: 'Ana malzeme',
      canContinueWithout: false
    };
  }

  if (isSkippable) {
    return {
      severity: 'skippable',
      label: 'Olmadan devam edilebilir',
      canContinueWithout: true
    };
  }

  return {
    severity: 'minor',
    label: 'Eksik malzeme',
    canContinueWithout: true
  };
};

export const enrichMissingIngredients = (missing = []) =>
  (Array.isArray(missing) ? missing : []).map((entry) => ({
    ...entry,
    missingSeverity: classifyMissingIngredient(entry)
  }));

export const buildMissingShoppingItems = (recipe = {}, missingOverride = null) => {
  const missing = Array.isArray(missingOverride)
    ? missingOverride
    : getRecipeMissingIngredients(recipe);
  const recipeName = recipe?.name || recipe?.title || 'Tarif';

  return missing
    .map((entry) => ({
      name: String(resolveName(entry) || '').trim(),
      amount: resolveAmount(entry),
      unit: resolveUnit(entry),
      dictionaryId: entry?.dictionaryId || entry?.dictionary_id || undefined,
      note: `Tarif: ${recipeName}`
    }))
    .filter((item) => item.name);
};
